import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useProjects } from "@/context/ProjectContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import ProjectsTable from "@/components/projects/ProjectsTable";
import { PlusCircle, Home, Folder, CalendarDays, User, MapPin, CheckCircle, AlertTriangle, Clock } from "lucide-react";
import { Roles } from "@/enums/roles.enum";
import { ProjectStatus } from "@/enums/project.enum";
import { useDeviceType } from "@/hooks/use-mobile";
import { MobileContainer, MobileSpacing } from "@/components/mobile/ResponsiveLayout";
import MobileCard from "@/components/mobile/MobileCard";
import { Badge } from "@/components/ui/badge";

const Projects = () => {
  const { projects, isLoading } = useProjects();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isMobile } = useDeviceType();

  const handleStatusClick = (status: string | null) => {
    if (!status) {
      // Clear all status filters and other filters
      setSearchParams({});
      return;
    }

    const currentStatusParam = searchParams.get("status");

    // If the same status is clicked again, deselect it (go to Total view)
    if (currentStatusParam === status) {
      setSearchParams({});
      return;
    }

    // Replace with the new single status
    const newParams = new URLSearchParams(searchParams);
    newParams.set("status", status);
    setSearchParams(newParams);
  };

  const isStatusSelected = (status: string | null) => {
    const currentStatusParam = searchParams.get("status");
    if (!status) {
      return !currentStatusParam; // Total is selected if no status param
    }
    return currentStatusParam === status;
  };

  const draftProjects = projects.filter(
    (proj) => proj.status === ProjectStatus.DRAFT
  ).length;
  const pendingProjects = projects.filter(
    (proj) => proj.status === ProjectStatus.PENDING
  ).length;
  const submittedProjects = projects.filter(
    (proj) => proj.status === ProjectStatus.SUBMITTED
  ).length;
  const inProgressProjects = projects.filter(
    (proj) => proj.status === ProjectStatus.IN_PROGRESS
  ).length;
  const completedProjects = projects.filter(
    (proj) => proj.status === ProjectStatus.COMPLETED
  ).length;
  const onHoldProjects = projects.filter(
    (proj) => proj.status === ProjectStatus.ON_HOLD
  ).length;
  const rejectedProjects = projects.filter(
    (proj) => proj.status === ProjectStatus.REJECTED
  ).length;
  const totalProjects = projects.length;

  const getStatusColor = (status: ProjectStatus) => {
    switch (status) {
      case ProjectStatus.PENDING:
        return "bg-yellow-100 text-yellow-800";
      case ProjectStatus.SUBMITTED:
        return "bg-blue-100 text-blue-800";
      case ProjectStatus.IN_PROGRESS:
        return "bg-purple-100 text-purple-800";
      case ProjectStatus.COMPLETED:
        return "bg-green-100 text-green-800";
      case ProjectStatus.ON_HOLD:
        return "bg-orange-100 text-orange-800";
      case ProjectStatus.REJECTED:
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (isMobile) {
    return (
      <MobileContainer>
        <div className="space-y-4">
          <nav className="flex items-center text-sm text-muted-foreground mb-1" aria-label="Breadcrumb">
            <Link to="/" className="hover:underline text-primary font-medium flex items-center">
              <Home className="h-4 w-4 mr-1" />
            </Link>
            <span className="mx-2">/</span>
            <span className="text-foreground font-semibold">Projects</span>
          </nav>

          <ProjectsTable 
            projects={projects} 
            isLoading={isLoading}
            title="Projects"
            showNewButton={user?.role === Roles.ADMIN || user?.role === Roles.VENDOR || user?.role === Roles.CLIENT}
          />

          {/* Bottom spacing for navigation */}
          <div className="h-20" />
        </div>
      </MobileContainer>
    );
  }

  // Desktop layout (original)
  return (
    <div className="space-y-6">
      <nav
        className="flex items-center text-sm text-muted-foreground mb-2"
        aria-label="Breadcrumb"
      >
        <Link
          to="/"
          className="hover:underline text-primary font-medium flex items-center"
        >
          <Home className="h-4 w-4 mr-1" />
        </Link>
        <span className="mx-2">/</span>
        <span className="text-foreground font-semibold">Projects</span>
      </nav>

      <div className="grid gap-4 grid-cols-2 md:grid-cols-4 lg:grid-cols-8">
        {[
          { id: "draft", label: "Draft", count: draftProjects, icon: Folder, color: "text-muted-foreground" },
          { id: "pending", label: "Pending", count: pendingProjects, icon: Folder, color: "text-status-pending" },
          { id: "submitted", label: "Submitted", count: submittedProjects, icon: Folder, color: "text-status-submitted" },
          { id: "in_progress", label: "In Progress", count: inProgressProjects, icon: Folder, color: "text-status-inProgress" },
          { id: "completed", label: "Completed", count: completedProjects, icon: CheckCircle, color: "text-status-approved" },
          { id: "on_hold", label: "On Hold", count: onHoldProjects, icon: Clock, color: "text-status-pending" },
          { id: "rejected", label: "Rejected", count: rejectedProjects, icon: AlertTriangle, color: "text-status-rejected" },
          { id: null, label: "Total", count: totalProjects, icon: Folder, color: "text-muted-foreground" },
        ].map((stat) => {
          const Icon = stat.icon;
          const isSelected = isStatusSelected(stat.id);
          
          return (
            <button
              key={stat.label}
              onClick={() => handleStatusClick(stat.id)}
              className={`relative flex flex-col gap-3 p-4 rounded-xl text-left transition-all duration-300 group border border-border/50 bg-card ${
                isSelected 
                  ? "bg-muted/30 ring-1 ring-primary/20 shadow-inner" 
                  : "shadow-sm hover:-translate-y-1 hover:shadow-lg hover:border-border/80"
              }`}
            >
              {/* Shiny Highlight for 3D feel */}
              <div className="absolute inset-x-4 top-0 h-[1px] bg-gradient-to-r from-transparent via-foreground/10 to-transparent opacity-50" />
              
              <div className="flex items-center justify-between w-full">
                <span className={`text-[10px] font-bold uppercase tracking-wider transition-colors ${
                  isSelected ? "text-foreground" : "text-muted-foreground/70"
                }`}>
                  {stat.label}
                </span>
                <Icon className={`h-4 w-4 transition-opacity group-hover:opacity-100 ${
                  isSelected ? "opacity-100" : "opacity-70"
                } ${stat.color}`} />
              </div>
              <div className="flex items-end gap-1">
                {isLoading ? (
                  <div className="h-8 w-16 bg-muted animate-pulse rounded" />
                ) : (
                  <span className="text-2xl font-bold tracking-tight text-foreground">
                    {stat.count}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      <ProjectsTable 
        projects={projects} 
        isLoading={isLoading}
        title="All Projects"
        showNewButton={user?.role === Roles.ADMIN || user?.role === Roles.VENDOR || user?.role === Roles.CLIENT}
      />
    </div>
  );
};

export default Projects;
